/**
 * Indicates the overall state of errors. Useful for determining if errors should be rendered and how
 * aggressive the error rendering should be.
 * - `none`: No errors.
 * - `partial`: Some errors exist, but things are not completely broken (e.g. at least one query does not have errors
 *    or has some data that may be worth showing).
 * - `critical`: There are errors and things are fully broken (e.g. all queries have errors and no data, the `VizPanel` has
 *   a validation error).
 */
export type ErrorState = 'none' | 'partial' | 'critical';

export const FALLBACK_ERROR_MESSAGE = 'Unknown error.';

/**
 * Error that has been processed to normalize the error message into a string and
 * de-duplicated with a count.
 */
export interface ProcessedError {
  errorMessage: string;
  /**
   * Number of instances of this error.
   */
  count: number;
}

const isProcessedError = (maybeError: unknown): maybeError is ProcessedError => {
  return (
    !!maybeError &&
    typeof maybeError === 'object' &&
    'errorMessage' in maybeError &&
    typeof maybeError.errorMessage === 'string' &&
    'count' in maybeError &&
    typeof maybeError.count == 'number'
  );
};

/**
 * Attempts to extract a meaningful error message `string` from an `unknown` value. Will return a
 * fallback message if none can be found. Useful for cases where we cannot guarantee all error messages
 * are `Error`s.
 */
export const getErrorMessageFromUnknown = (maybeError: unknown | ProcessedError): string => {
  if (typeof maybeError === 'string') {
    return maybeError || FALLBACK_ERROR_MESSAGE;
  }

  if (isProcessedError(maybeError)) {
    if (maybeError.count > 1) {
      return `${maybeError.errorMessage} (${maybeError.count} occurrences)`;
    }

    return maybeError.errorMessage;
  }

  // Type guarding to attempt to check for a message on a standard `Error` or `Notice` object.
  if (
    maybeError &&
    typeof maybeError === 'object' &&
    'message' in maybeError &&
    typeof maybeError.message === 'string'
  ) {
    return maybeError.message || FALLBACK_ERROR_MESSAGE;
  }

  // Unable to find a meaningful error message. Return fallback message.
  return FALLBACK_ERROR_MESSAGE;
};
